import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  BookOpen, 
  BarChart3,
  Lightbulb,
  Award,
  Calendar,
  Play
} from 'lucide-react';
import { generateRevisionInsights, calculateStudyStreak, formatDuration } from '../utils/revisionUtils';

const RevisionInsights = ({ userId, onStartPractice, onStartRevision }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      loadInsights();
    }
  }, [userId]);

  const loadInsights = async () => {
    try {
      setLoading(true);
      const result = await generateRevisionInsights(userId);
      if (result.success) {
        setInsights(result.data);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-6">
        <div className="text-center text-gray-500">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="card p-6">
        <div className="text-center text-gray-500">
          <BookOpen className="w-8 h-8 mx-auto mb-2" />
          <p>No revision data available. Start practicing to see insights!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          Overall Progress
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {insights.overallProgress.totalQuestions}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Questions Answered</div>
          </div>
          
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {insights.overallProgress.averageAccuracy}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Average Accuracy</div>
          </div>
          
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {insights.overallProgress.totalSessions}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Practice Sessions</div>
          </div>
        </div>
      </motion.div>

      {/* Recommendations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Lightbulb className="w-5 h-5 mr-2" />
          Smart Recommendations
        </h3>
        
        <div className="space-y-3">
          {insights.recommendations.length > 0 ? (
            insights.recommendations.map((recommendation, index) => (
              <div 
                key={index}
                className={`p-4 rounded-lg border-l-4 ${
                  recommendation.priority === 'high' 
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                    : 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {recommendation.message}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {recommendation.action}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (recommendation.type === 'focus_weak_topic') {
                        onStartPractice(insights.weakTopics[0].name);
                      } else if (recommendation.type === 'more_practice') {
                        onStartRevision();
                      }
                    }}
                    className="ml-4 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    <Play className="w-3 h-3 inline mr-1" />
                    Start
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-4">
              <CheckCircle className="w-8 h-8 mx-auto mb-2" />
              <p>Great job! No urgent recommendations at the moment.</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Weak Topics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2" />
          Areas Needing Focus
        </h3>
        
        <div className="space-y-3">
          {insights.weakTopics.length > 0 ? (
            insights.weakTopics.map((topic, index) => (
              <div key={topic.name} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {topic.name}
                  </h4>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-sm text-red-600 dark:text-red-400">
                      {topic.accuracy}% accuracy
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {topic.totalQuestions} questions
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => onStartPractice(topic.name)}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                >
                  Practice
                </button>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-4">
              <CheckCircle className="w-8 h-8 mx-auto mb-2" />
              <p>No weak topics identified. Keep up the good work!</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Strong Topics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Award className="w-5 h-5 mr-2" />
          Your Strengths
        </h3>
        
        <div className="space-y-3">
          {insights.strongTopics.length > 0 ? (
            insights.strongTopics.map((topic, index) => (
              <div key={topic.name} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {topic.name}
                  </h4>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-sm text-green-600 dark:text-green-400">
                      {topic.accuracy}% accuracy
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {topic.totalQuestions} questions
                    </span>
                  </div>
                </div>
                <div className="text-green-600 dark:text-green-400">
                  <CheckCircle className="w-5 h-5" />
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-4">
              <Target className="w-8 h-8 mx-auto mb-2" />
              <p>Keep practicing to identify your strong areas!</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Improvement Areas */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2" />
          Improvement Areas
        </h3>
        
        <div className="space-y-3">
          {insights.improvementAreas.length > 0 ? (
            insights.improvementAreas.map((topic, index) => (
              <div key={topic.name} className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {topic.name}
                  </h4>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-sm text-yellow-600 dark:text-yellow-400">
                      {topic.accuracy}% accuracy
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {topic.totalQuestions} questions
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => onStartPractice(topic.name)}
                  className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                >
                  Practice
                </button>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-4">
              <TrendingUp className="w-8 h-8 mx-auto mb-2" />
              <p>No improvement areas identified at the moment.</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default RevisionInsights;
